import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Link,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Card,
  Divider
} from "@mui/material";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, getFirebaseErrorMessage } from "../../../firebase/firebase"; 
import { ForgotPasswordModal } from "./ForgotPasswordModal";
import { Visibility, VisibilityOff } from "@mui/icons-material";

export const SignInForm = ({ strings = {} }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotPasswordModal, setForgotPasswordModal] = useState(false);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      setError("");
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.log("Firebase login error", error.code);
      setError(getFirebaseErrorMessage(error.code));
    } finally {
      setLoading(false);
    }
  };

  const handleForgetPasswordModalClose = () => {
    setForgotPasswordModal(false);
  };
    
  const primaryColor = "#1976d2"; // A professional blue

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column', // Changed to column to stack card and footer
      alignItems: 'center',
      justifyContent: 'center',
      p: 2,
      background: '#'
    }}>
      <Card
        component="form"
        onSubmit={handleSignIn}
        sx={{ 
          p: { xs: 2, sm: 3, md: 4 }, 
          maxWidth: '960px', 
          width: '100%', 
          boxShadow: "0 1px 3px rgba(60,64,67,.30), 0 4px 8px rgba(60,64,67,.15)", 
          overflow: 'hidden',          
          borderRadius: 2,
        }}
      >
        <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
          {/* Left Column for Branding */}
          <Grid item xs={12} md={5}>
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Box
                component="img"
                src="ct_logo_black.png"
                alt="CandidateTools logo"
                sx={{ width: '100%', maxWidth: '250px', height: 'auto', mb: 3 }}
                onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/250x60/000000/FFFFFF?text=CandidateTools&font=roboto'; }}
              />
                <Typography variant="h4" component="h1" sx={{ mb: 1.5, fontWeight: 600 }}>
                  {strings.signInHeader || 'Sign in'}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {strings.signInSubtitle || 'to access your dashboard'}
                </Typography>
              </Box>
            </Grid>

            {/* Right Column for the form */}
            <Grid item xs={12} md={7}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                }}
              >
                <TextField
                  label={strings.emailLabel || 'Email Address'}
                  type="email"
                  fullWidth
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                />
                <TextField
                  label={strings.passwordLabel || 'Password'}
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword((v) => !v)} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                
                <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: 'center', mt: 1 }}>
                  <Link component="button" type="button" variant="body2" sx={{ color: primaryColor, fontWeight: 500 }} onClick={() => setForgotPasswordModal(true)}>
                    {strings.forgotPassword || 'Forgot password?'}
                  </Link>
                </Box>

                {error && <Alert severity="error" sx={{ mt: 0, mb: 1 }} onClose={() => setError("")}>{error}</Alert>}

                <Button 
                  type="submit" 
                  variant="contained" 
                  size="large"
                  sx={{ 
                    bgcolor: primaryColor, 
                    '&:hover': { bgcolor: '#1565c0' }, 
                    py: 1.5,
                    textTransform: 'none',
                    fontSize: '1rem',
                    borderRadius: '8px'
                  }} 
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : (strings.nextButton || 'Sign In')}
                </Button>

                <Typography variant="body2" sx={{ textAlign: 'center', mt: 2, color: 'text.secondary' }}>
                  Don't have an account?{' '} this is for debugging (remove when we launch) 
                  <Link component="button" type="button" sx={{ color: primaryColor, fontWeight: 500 }} onClick={() => navigate('/signup')}>
                    {strings.switchToSignUp || 'Create one now'}
                  </Link>
                </Typography>
              </Box>
            </Grid>
        </Grid>
        <ForgotPasswordModal
          forgotPasswordModal={forgotPasswordModal}
          handleForgetPasswordModalClose={handleForgetPasswordModalClose}
        />
      </Card>
      {/* Footer Section */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2.5, mt: 4 }}>
          <Link href="/help" variant="body2" color="text.secondary" sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' }}}>Help</Link>
          <Link href="/privacy" variant="body2" color="text.secondary" sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' }}}>Privacy</Link>
          <Link href="/terms" variant="body2" color="text.secondary" sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' }}}>Terms</Link>
      </Box>
    </Box>
  );
};